import { defineStore } from 'pinia'

import type { TutorApplicationState as Form } from './interfaces'

import { ReferralSource } from '../enums'

const initialPersonalInformation = () => ({
  fullName: '',
  email: '',
  emailValid: false,
  phoneNumber: '',
}) satisfies Partial<Form>

const initialAdditionalQuestions = () => ({
  nationality: '',
  ageConfirmation: null,
  recommended: null,
  referrerFullName: '',
  referralSource: null,
  jobPostingSite: '',
  hasBachelorDegree: null,
  teachingExperienceYears: null,
  workRequiredSchedule: null,
  onboardingTimeframe: null,
}) satisfies Partial<Form>

const initialLegalAgreements = () => ({
  acceptEmailDelivery: false,
  acceptConfidentiality: false,
  acceptNotRestrictedResident: false,
  acceptDataProcessingTerms: false,
}) satisfies Partial<Form>

const initialCv = () => ({
  cv: null,
}) satisfies Partial<Form>

const initialCoverLetter = () => ({
  coverLetter: null,
}) satisfies Partial<Form>

const initialForm = (): Form => ({
  ...initialPersonalInformation(),
  ...initialCv(),
  ...initialCoverLetter(),
  ...initialAdditionalQuestions(),
  ...initialLegalAgreements(),
})

export const useTutorApplicationStore = defineStore('tutorApplication', () => {
  const form = reactive<Form>(initialForm())
  const showErrors = ref(false)
  // belongs to the session, not to the form — survives resetForm
  const amplitudeId = ref<string | null>(null)

  const isMainInformationValid = computed(() => (
    !!form.fullName &&
    !!form.email &&
    form.emailValid &&
    !!form.phoneNumber &&
    !!form.cv &&
    !!form.coverLetter
  ))

  const isAdditionQuestionsValid = computed(() => {
    const isReferralValid = form.recommended ? !!form.referrerFullName : true
    const isReferralSourceValid = form.referralSource === ReferralSource.JobBoard ? !!form.jobPostingSite : true

    return (
      form.nationality !== null &&
      form.ageConfirmation !== null &&
      form.recommended !== null &&
      isReferralValid &&
      !!form.referralSource &&
      isReferralSourceValid &&
      form.hasBachelorDegree !== null &&
      !!form.teachingExperienceYears &&
      form.workRequiredSchedule !== null &&
      !!form.onboardingTimeframe
    )
  })

  const isLegalAgreementsValid = computed(() => (
    form.acceptEmailDelivery &&
    form.acceptConfidentiality &&
    form.acceptNotRestrictedResident &&
    form.acceptDataProcessingTerms
  ))

  const isValid = computed(() => isMainInformationValid.value && isAdditionQuestionsValid.value && isLegalAgreementsValid.value)

  const setField = <K extends keyof Form>(key: K, value: Form[K]): void => {
    form[key] = value
  }
  const setShowErrors = (payload: boolean): void => {
    showErrors.value = payload
  }
  const setAmplitudeId = (payload: string | null): void => {
    amplitudeId.value = payload
  }

  const resetPersonalInformation = (): void => {
    Object.assign(form, initialPersonalInformation())
    showErrors.value = false
  }
  const resetCv = (): void => {
    Object.assign(form, initialCv())
    showErrors.value = false
  }
  const resetCoverLetter = (): void => {
    Object.assign(form, initialCoverLetter())
    showErrors.value = false
  }
  const resetAdditionalQuestions = (): void => {
    Object.assign(form, initialAdditionalQuestions())
    showErrors.value = false
  }
  const resetLegalAgreements = (): void => {
    Object.assign(form, initialLegalAgreements())
    showErrors.value = false
  }
  const resetForm = (): void => {
    Object.assign(form, initialForm())
    showErrors.value = false
  }

  const reset = (): void => {
    resetForm()
    amplitudeId.value = null
  }

  return {
    form,
    showErrors,
    amplitudeId,
    isValid,
    setField,
    setShowErrors,
    setAmplitudeId,
    resetPersonalInformation,
    resetCv,
    resetCoverLetter,
    resetAdditionalQuestions,
    resetLegalAgreements,
    resetForm,
    reset,
  }
})
